import type { DeviceDto } from '@onig-control/contracts';
import { ArrowLeft, Pencil } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ApiClientError, api } from '../api';
import { Empty, ErrorMessage, PageHeader, StatusBadge } from '../components';
import {
  deviceTypes,
  deviceExecutions,
  deviceHostings,
  deviceFormFactors,
} from '../device-classification';
import { DeviceForm } from '../DeviceForm';
import { DeviceLifecycle } from '../DeviceLifecycle';
import { DeviceStockStatus, DeviceDeskAccess } from '../DeviceStatus';
import { usePermissions } from '../permissions';
import { auditFields, auditLabel } from './ActivityPage';
import type { Assignment, AuditEvent, ExternalIdentity } from '../types';

type Tab = 'overview' | 'integrations' | 'activity';
export function DeviceDetailPage() {
  const { id = '' } = useParams();
  const can = usePermissions();
  const allowed = can('core:devices:read');
  const [device, setDevice] = useState<DeviceDto>();
  const [error, setError] = useState<unknown>();
  const [editing, setEditing] = useState(false);
  const [tab, setTab] = useState<Tab>('overview');
  const load = useCallback(async () => {
    setDevice(await api<DeviceDto>(`/api/devices/${id}`));
  }, [id]);
  useEffect(() => {
    if (allowed) void load().catch(setError);
  }, [load, allowed]);
  if (!allowed) return <Empty>Keine Berechtigung zum Lesen von Geräten.</Empty>;
  if (!device)
    return (
      <>
        <ErrorMessage error={error} />
        {!error && <Empty>Gerät wird geladen…</Empty>}
      </>
    );
  return (
    <>
      <Link to="/devices" className="mb-4 inline-flex items-center gap-1 text-sm text-muted">
        <ArrowLeft size={15} />
        Geräte
      </Link>
      <PageHeader
        title={device.name}
        description={[deviceTypes[device.deviceType], device.hostname, device.company?.name]
          .filter(Boolean)
          .join(' · ')}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <DeviceStockStatus status={device.status} />
            <DeviceDeskAccess reports={device.deviceDesk} />
            {can('core:devices:write') && device.status !== 'ARCHIVED' && (
              <>
                <button
                  className="button-secondary"
                  onClick={() => {
                    setEditing(true);
                    setTab('overview');
                  }}
                >
                  <Pencil size={15} />
                  Bearbeiten
                </button>

              </>
            )}
          </div>
        }
      />
      <ErrorMessage error={error} />
      {can('core:devices:write') && <DeviceLifecycle device={device} onChanged={load} />}
      <div className="mb-5 flex gap-1 border-b border-line">
        {(
          [
            ['overview', 'Übersicht'],
            ['integrations', 'Integrationen'],
            ['activity', 'Aktivität'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            className={`border-b-2 px-4 py-2 text-sm ${tab === key ? 'border-accent text-accent' : 'border-transparent text-muted'}`}
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === 'overview' &&
        (editing ? (
          <DeviceForm
            key={device.revision}
            device={device}
            onCancel={() => setEditing(false)}
            onSave={async (input) => {
              try {
                await api(`/api/devices/${id}`, {
                  method: 'PATCH',
                  revision: device.revision,
                  body: JSON.stringify(input),
                });
                await load();
                setEditing(false);
                setError(undefined);
              } catch (e) {
                if (e instanceof ApiClientError && e.status === 409) {
                  await load();
                  setEditing(false);
                  setError(e);
                }
                throw e;
              }
            }}
          />
        ) : (
          <div className="space-y-5">
            {device.deviceDesk?.some((report) => report.accessStatus === 'REVOKED') && <DeviceDeskAccess reports={device.deviceDesk} detail />}
            <div className="grid gap-5 xl:grid-cols-[2fr_1fr]">
              <section className="panel p-6">
                <h2 className="section-title">Geräteidentität</h2>
                <dl className="grid gap-5 sm:grid-cols-2">
                  {[
                    ['Gerätetyp', deviceTypes[device.deviceType]],
                    ['Ausführung', deviceExecutions[device.execution]],
                    ['Betriebsort', deviceHostings[device.hosting]],
                    [
                      'Bauform',
                      device.formFactor !== 'UNKNOWN' ? deviceFormFactors[device.formFactor] : null,
                    ],
                    [
                      'Rolle',
                      device.roles.includes('VIRTUALIZATION_HOST') ? 'Virtualisierungshost' : null,
                    ],
                    ['Anbieter / Betreiber', device.hostingProvider],
                    ['Region / Rechenzentrum', device.hostingRegion],
                    ['Hostname', device.hostname],
                    ['Hersteller', device.manufacturer],
                    ['Modell', device.model],
                    ['Seriennummer', device.serialNumber],
                    ['Inventarnummer', device.assetTag],
                  ]
                    .filter(([, value]) => value)
                    .map(([label, value]) => (
                      <div key={label}>
                        <dt className="label">{label}</dt>
                        <dd className="break-words text-sm">{value}</dd>
                      </div>
                    ))}
                </dl>
              </section>
              <section className="panel p-6">
                <h2 className="section-title">Zuordnung</h2>
                <dl className="space-y-5">
                  <div>
                    <dt className="label">Kunde</dt>
                    <dd className="text-sm">
                      {device.company ? (
                        <Link
                          className="text-accent hover:underline"
                          to={`/companies/${device.company.id}`}
                        >
                          {device.company.name}
                        </Link>
                      ) : (
                        'Nicht zugeordnet'
                      )}
                    </dd>
                  </div>
                  {device.site && (
                    <div>
                      <dt className="label">Standort</dt>
                      <dd className="text-sm">{device.site.name}</dd>
                    </div>
                  )}
                  {device.responsibleContact && (
                    <div>
                      <dt className="label">Verantwortlicher Kontakt</dt>
                      <dd className="text-sm">{device.responsibleContact.displayName}</dd>
                    </div>
                  )}
                </dl>
                {!device.company && (
                  <p className="mt-4 text-xs text-muted">
                    Über „Bearbeiten“ einem Kunden zuordnen.
                  </p>
                )}
              </section>
            </div>
            <details className="technical">
              <summary>Technische Metadaten</summary>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <p className="break-all">Control-ID: {device.id}</p>
                <p>Revision: {device.revision}</p>
                <p>Erstellt: {new Date(device.createdAt).toLocaleString('de-DE')}</p>
                <p>Geändert: {new Date(device.updatedAt).toLocaleString('de-DE')}</p>
                {device.archivedAt && (
                  <p>Archiviert: {new Date(device.archivedAt).toLocaleString('de-DE')}</p>
                )}
              </div>
            </details>
          </div>
        ))}
      {tab === 'integrations' && <DeviceIntegrations device={device} />}
      {tab === 'activity' && <DeviceActivity id={id} />}
    </>
  );
}

function DeviceIntegrations({ device }: { device: DeviceDto }) {
  const can = usePermissions();
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [identities, setIdentities] = useState<ExternalIdentity[]>([]);
  const [error, setError] = useState<unknown>();
  const read = can('core:integrations:read'), readMappings = can('core:external-identities:read');
  useEffect(() => {
    const abort = new AbortController();
    if (read) void api<Assignment[]>(`/api/entities/DEVICE/${device.id}/assignments`, { signal: abort.signal }).then(setAssignments).catch((e) => { if (!abort.signal.aborted) setError(e); });
    if (readMappings) void api<ExternalIdentity[]>(`/api/entities/DEVICE/${device.id}/external-identities`, { signal: abort.signal }).then(setIdentities).catch((e) => { if (!abort.signal.aborted) setError(e); });
    return () => abort.abort();
  }, [device.id, device.revision, read, readMappings]);
  if (!read) return <Empty>Keine Berechtigung zum Lesen von Integrationen.</Empty>;
  return <div className="space-y-4"><ErrorMessage error={error} />
    <p>Zum Beenden einer App-Verwaltung „Gerät verwalten“ verwenden. Neue Installationen werden in der jeweiligen App registriert.</p>
    <div className="panel overflow-x-auto"><table className="table"><thead><tr><th>App</th><th>Freigabe</th><th>Abgleich</th></tr></thead><tbody>{assignments.map((item) => <tr key={item.id}><td>{item.instance.name}</td><td><StatusBadge status={item.status} /></td><td><StatusBadge status={item.provisioning?.status ?? 'NOT_CONFIGURED'} /></td></tr>)}</tbody></table>{!assignments.length && <Empty>Keine Apps zugeordnet.</Empty>}</div>
    {readMappings && <div className="panel p-5"><h2 className="font-semibold">Verknüpfungen und frühere Installationen</h2><ul className="my-3 space-y-2">{identities.map((item) => <li key={item.id}>{item.provider} · {item.bindingState === 'SUPERSEDED' ? 'Ersetzt' : item.bindingState === 'REMOVED' ? 'Beendet' : 'Verknüpft'} · Generation {item.generation ?? 1}<span className="ml-2 font-mono text-xs">{item.externalId}</span></li>)}</ul></div>}
  </div>;
}

function DeviceActivity({ id }: { id: string }) {
  const can = usePermissions();
  const allowed = can('core:audit:read');
  const [items, setItems] = useState<AuditEvent[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<unknown>();
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!allowed) return;
    const controller = new AbortController();
    setLoading(true);
    void api<{ items: AuditEvent[]; total: number }>(
      `/api/audit?entityType=DEVICE&entityId=${id}&page=${page}`,
      { signal: controller.signal },
    )
      .then((result) => {
        setItems(result.items);
        setTotal(result.total);
        setError(undefined);
      })
      .catch((e) => {
        if (!controller.signal.aborted) setError(e);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [allowed, id, page]);
  if (!allowed) return <Empty>Keine Berechtigung zum Lesen des Audit Trails.</Empty>;
  return (
    <>
      <ErrorMessage error={error} />
      <div className="panel divide-y divide-line">
        {loading ? (
          <Empty>Aktivität wird geladen…</Empty>
        ) : !items.length ? (
          <Empty>Keine Aktivitäten vorhanden.</Empty>
        ) : (
          items.map((item) => (
            <div className="p-4 text-sm" key={item.id}>
              <span className="font-semibold">{item.actorDisplayName ?? item.actorId}</span> ·{' '}
              {auditLabel(item)}
              <div className="mt-1 text-xs text-muted">
                {new Date(item.createdAt).toLocaleString('de-DE')} · {auditFields(item)}
              </div>
            </div>
          ))
        )}
      </div>
      <div className="mt-3 flex gap-2">
        <button
          className="button-secondary"
          disabled={loading || page === 1}
          onClick={() => setPage(page - 1)}
        >
          Zurück
        </button>
        <button
          className="button-secondary"
          disabled={loading || page * 25 >= total}
          onClick={() => setPage(page + 1)}
        >
          Weiter
        </button>
      </div>
    </>
  );
}
